/**
 * DI-3-5 — the Analysis header's Flights section (story §2.3): under the
 * asset facts, above the widgets; beside the three-dots menu, never inside it.
 *
 * One row per flight (lyse/v1 8.6.0 `LyseFlight`, every row incl. disabled and
 * merged-away, in `flightNo` order as served — DI-3-S63, lyse/v1 8.9.0):
 *   date (the window's UTC day — "two days on one turbine" shows two dates,
 *   DI-D39) · window · offset + route + residual · azimuth / ε · images ·
 *   on / off with the reason · "merged · n flights" (`mergedFrom`) or the muted
 *   "merged into flight n" (`mergedInto`) · "another operator's log" when
 *   `foreign` (DI-D56 — there is no serial or firmware line to show; this
 *   section renders neither for ANY flight).
 * Then the `blobStateCounts` line (DI-D25 c4: withheld normal, missing a defect).
 *
 * `FlightDateHint` is the "first day of n" beside the acquisition's flight
 * date when the flights span several days (Intake Workflows v2 §7).
 */
import type { ReactElement } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type { LyseBlobStateCounts, LyseFlight } from "@aeriosiq/types";
import { FlightChip } from "../data-acquisitions/FlightChips.js";
import {
  blobStateLines,
  flightDay,
  flightDays,
  formatDegrees,
  formatImages,
  formatOffset,
  formatResidual,
  formatRoute,
  formatWindow,
  FOREIGN_LOG_WORDS,
} from "../data-acquisitions/flightFacts.js";

/** "merged · 2 flights" — the intake's words (apps/web intake/flights/merge.ts). */
function mergedChipLabel(f: LyseFlight): string {
  return `merged · ${String((f.mergedFrom?.length ?? 0) + 1)} flights`;
}

function mergedIntoLabel(seq: number | null): string {
  return seq === null
    ? "merged into another flight"
    : `merged into flight ${String(seq)}`;
}

export function FlightDateHint({
  flights,
}: {
  flights: readonly LyseFlight[] | undefined;
}): ReactElement | null {
  const days = flightDays(flights ?? []);
  if (days.length < 2) return null;
  return (
    <Typography
      component="span"
      data-testid="flight-date-hint"
      sx={{
        ml: 1,
        color: "text.secondary",
        fontSize: typography.size["label-md"],
      }}
    >
      first day of {days.length}
    </Typography>
  );
}

const SEP = " · ";

function FlightRow({
  flight: f,
  seqOf,
}: {
  flight: LyseFlight;
  seqOf: ReadonlyMap<string, number>;
}): ReactElement {
  const absorbed = Boolean(f.mergedInto);
  const offsetLine = [
    formatOffset(f.timeOffsetS),
    formatRoute(f.timeRoute),
    formatResidual(f.timeResidualM),
  ].join(SEP);
  return (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      data-testid="flight-row"
      data-muted={absorbed ? "true" : "false"}
      sx={{
        fontSize: typography.size["label-md"],
        color: absorbed ? "text.secondary" : "text.primary",
        opacity: absorbed ? 0.7 : 1,
      }}
    >
      <Box component="span" sx={{ fontWeight: typography.weight.semibold }}>
        Flight {f.flightNo}
      </Box>
      <span>{flightDay(f.windowStartUtc) ?? "—"}</span>
      <span>{formatWindow(f.windowStartUtc, f.windowEndUtc)}</span>
      <span>{offsetLine}</span>
      <span>
        az {formatDegrees(f.azimuth)}
        {SEP}ε {formatDegrees(f.epsilon)}
      </span>
      <span>{formatImages(f.imageCount ?? 0)}</span>
      {absorbed ? (
        <Box component="span" sx={{ fontStyle: "italic" }}>
          {mergedIntoLabel(
            f.mergedInto ? (seqOf.get(f.mergedInto) ?? null) : null,
          )}
        </Box>
      ) : f.enabled ? (
        <FlightChip label="on" slot="success" />
      ) : (
        <FlightChip
          label={f.disabledReason ? `off — ${f.disabledReason}` : "off"}
          slot="neutral"
        />
      )}
      {f.mergedFrom && f.mergedFrom.length > 0 ? (
        <FlightChip label={mergedChipLabel(f)} slot="neutral" />
      ) : null}
      {f.foreign ? (
        <FlightChip label={FOREIGN_LOG_WORDS} slot="neutral" />
      ) : null}
    </Stack>
  );
}

export function FlightsSection({
  flights,
  blobStateCounts,
}: {
  flights?: readonly LyseFlight[] | undefined;
  blobStateCounts?: LyseBlobStateCounts | undefined;
}): ReactElement | null {
  if (flights === undefined && blobStateCounts === undefined) return null;
  const seqOf = new Map((flights ?? []).map((f) => [f.flightId, f.flightNo]));
  const blob = blobStateLines(blobStateCounts);
  return (
    <Box
      component="section"
      aria-label="Flights"
      data-testid="flights-section"
      sx={{ borderTop: 1, borderColor: "divider", pt: 0.5 }}
    >
      <Stack spacing={0.5}>
        <Typography
          component="h3"
          sx={{
            fontSize: typography.size["label-md"],
            letterSpacing: typography.tracking["label-md"],
            color: "text.secondary",
            textTransform: "uppercase",
          }}
        >
          Flights
        </Typography>
        {flights === undefined ? null : flights.length === 0 ? (
          <Typography
            component="p"
            sx={{
              fontSize: typography.size["label-md"],
              color: "text.secondary",
            }}
          >
            no flight recorded
          </Typography>
        ) : (
          flights.map((f) => (
            <FlightRow key={f.flightId} flight={f} seqOf={seqOf} />
          ))
        )}
        {blob.length > 0 ? (
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            data-testid="blob-state-line"
          >
            <Typography
              component="span"
              sx={{
                fontSize: typography.size["label-md"],
                color: "text.secondary",
                mr: 0.5,
              }}
            >
              Image bytes:
            </Typography>
            {blob.map((l) => (
              <FlightChip
                key={l.state}
                label={l.label}
                slot={l.slot}
                testId={`blob-state-${l.state}`}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
